/**
 * The arithmetic of the deck editor's focus positioning — ADR-067 Decision 4,
 * as amended twice on 2026-07-30 and then restated in full by Decision A of
 * a57f293 and sharpened the same day.
 *
 * Pure functions over rects, deliberately: they take the band as measured by
 * the caller (DeckMobileFeed's `measureFreeSpace`, which reads live chrome, as
 * ADR-067 requires) and return a signed scroll delta. Nothing here touches the
 * DOM beyond reading geometry, nothing here scrolls, and nothing here is
 * consulted again once the motion has landed — the placement is an assistance
 * offered once at focus, not a constraint on where the user may scroll
 * afterwards, which the second amendment states as a principle.
 *
 * They live in their own module rather than in DeckMobileFeed so the invariant
 * can be asserted directly (test/DeckFocusPositioning.test.ts) without laying
 * out the component in jsdom, which cannot size the real rows.
 */

/** Comfortable clearance a focused field must keep at both edges of the band
 * before it counts as "already placed" (design system --space-4). A field
 * resting exactly on an edge — flush against the keyboard, tucked under the
 * top strip — is still readable-adjacent, not readable. */
export const FOCUS_CLEARANCE = 16

/** How much of the element ABOVE the focused one is kept on screen in the one
 * case where the focused field and both its neighbours cannot fit the band at
 * once. Enough to be a tap target and no more: the band that remains belongs to
 * the field below, which Decision A prefers because forward movement through a
 * card is the common direction.
 *
 * Reachable as of the change that made a field's height its content's height
 * regardless of focus — before it, every unfocused field was one row and three
 * of them always fit. Exported so the tests assert the shipped number rather
 * than a copy of it. */
export const NEIGHBOUR_MIN_REVEAL = 28

export interface Band { top: number; bottom: number }

/**
 * The band's usable edges — `free` minus FOCUS_CLEARANCE, clamped so the two
 * clearances can never invert on a band narrower than twice the clearance
 * (which would otherwise make the rule fight itself). Neither the clearance nor
 * `free` is changed here; this only names the pair the placement is measured
 * against, so the two functions below cannot drift apart on it.
 */
function usableBand(free: Band): Band {
  const mid = (free.top + free.bottom) / 2
  return {
    top: Math.min(free.top + FOCUS_CLEARANCE, mid),
    bottom: Math.max(free.bottom - FOCUS_CLEARANCE, mid),
  }
}

/**
 * Where the page must scroll to place the active CARD for a caret in `row`, as
 * a signed delta (positive scrolls the content up). ADR-067 Decision A as
 * sharpened: the card is the positioning unit, and on a card taller than the
 * band the invariant is that the elements immediately before and after the
 * focused one are both on screen and directly tappable — that is what lets a
 * repeated tap in one spot walk the card a field at a time. Since Enter is a
 * line break in side-B fields again, a tap is the only route between fields, so
 * a neighbour left off screen has no route to it at all.
 *
 * Neighbours are read as DOM siblings inside the card rather than as a list of
 * fields, which is exactly right for the `+ Field` / `Fill` row: it is the
 * element following the last field and is reached by the same tap, not chrome
 * to be excluded. Field heights are read as they are and never equalised or
 * padded to make the rule hold — the amendment forbids that by name, and it is
 * why the tap target shifts by the difference between two adjacent field
 * heights and by no more.
 */
export function focusDelta(row: HTMLElement, card: HTMLElement | null, free: Band): number {
  const rect = row.getBoundingClientRect()

  // The one surviving case for top alignment, unchanged since Decision 4: a
  // field taller than the band cannot be shown whole at any scroll position, so
  // it is read from its beginning and grows downward (caretKeepDelta then keeps
  // the caret in view as it does). The invariant above cannot hold for it.
  if (rect.height > free.bottom - free.top) return rect.top - free.top

  const band = usableBand(free)
  const bandHeight = band.bottom - band.top
  const cardRect = (card ?? row).getBoundingClientRect()

  // A card that fits the band is brought fully inside it — and once it is
  // inside, every field of it is already reachable, so a tap moves nothing at
  // all. This is the clause the sharpening left standing verbatim.
  if (cardRect.height <= bandHeight) {
    if (cardRect.top < band.top) return cardRect.top - band.top
    if (cardRect.bottom > band.bottom) return cardRect.bottom - band.bottom
    return 0
  }

  // Taller than the band. The span of the focused element and its two immediate
  // neighbours is centred, which is what makes the placement stable: the
  // focused field lands in the same region of the band whichever field it is,
  // so the spot just below it is always the next field and the spot just above
  // is always the previous one.
  const prev = row.previousElementSibling as HTMLElement | null
  const next = row.nextElementSibling as HTMLElement | null
  const spanTop = prev ? prev.getBoundingClientRect().top : rect.top
  const spanBottom = next ? next.getBoundingClientRect().bottom : rect.bottom

  let delta: number
  if (spanBottom - spanTop <= bandHeight) {
    delta = (spanTop + spanBottom) / 2 - (band.top + band.bottom) / 2
  } else {
    // The three cannot fit at once. Decision A's stated preference decides it:
    // the field below gets the room, and the one above keeps a tappable sliver
    // so the walk still has a way back up.
    const reveal = Math.min(NEIGHBOUR_MIN_REVEAL, rect.top - spanTop)
    delta = rect.top - (band.top + reveal)
  }

  // The centring is kept from spending band on what lies OUTSIDE the card: at
  // the card's first field it resolves to card-top-against-band-top, at its last
  // to card-bottom-against-band-bottom, which is the most of the card either end
  // can show. This bounds the number this function returns, once, at placement
  // time; it is emphatically NOT a scroll bound — nothing here is consulted
  // again after the motion lands, and the page stays free to be scrolled
  // anywhere in either direction while the field holds focus. The two
  // amendments that forbid a bound forbid one applied to the user's gestures.
  // The bounds cannot invert: the card is taller than the band in this branch.
  return Math.max(Math.min(delta, cardRect.bottom - band.bottom), cardRect.top - band.top)
}

/**
 * The much smaller correction applied while typing: enough to keep the caret
 * inside the band as the field grows, and nothing else. Downward only and never
 * a placement — Decision A records that the card is positioned on activation
 * and that growth is not a repositioning event, so this may not pull a card the
 * author has scrolled back to where activation left it.
 *
 * `caretBottom` is the lowest point that must stay visible for the caret to be
 * in view, in viewport coordinates — `caretGeometry.focusedCaretKeepBottom`
 * measures it. Omitted or `null`, the row's own bottom stands in for it.
 *
 * That stand-in is exact for a caret at the END of the text, where the caret
 * rides the field's bottom edge as it grows, and it was the only rule here
 * until a newline pressed in the MIDDLE of a long field showed what it costs
 * elsewhere: the field grows downward, its bottom leaves the band by a line,
 * and keeping that bottom in view scrolls the whole remainder of the field into
 * the band — landing the page at the field's end with the caret off screen
 * above it, to be scrolled back to by hand. The field's bottom is not the caret
 * unless the caret is on the last line, and this function now says so.
 *
 * The `min` is what makes the refinement one-directional. Whatever the caret
 * measurement reports, this never asks for MORE scrolling than the row-bottom
 * rule would have: a caret on the last line resolves to the row's bottom, so
 * appending at the end moves the page by exactly what it always did, and a
 * caret anywhere above it asks for less. There is no input for which the new
 * rule scrolls further than the old one.
 */
export function caretKeepDelta(row: HTMLElement, free: Band, caretBottom?: number | null): number {
  const band = usableBand(free)
  const rowBottom = row.getBoundingClientRect().bottom
  const keep = caretBottom == null ? rowBottom : Math.min(rowBottom, caretBottom)
  return Math.max(0, keep - band.bottom)
}
